<template>
  <div class="theme-dict page-shell">
    <!-- 顶部：说明 + 新建入口 -->
    <div class="dict-header">
      <div class="dict-intro">
        <div class="intro-title">题材字典</div>
        <div class="intro-desc">
          题材是打标的基础设施：先定义「什么算、什么不算」，再打标。
          语义重复的题材用「合并」迁移个股打标，弃用但不删除数据。
        </div>
        <div class="intro-stats">
          <span>生效题材 <b>{{ activeCount }}</b></span>
          <span>已停用 <b>{{ inactiveCount }}</b></span>
          <span>打标记录 <b>{{ totalTags }}</b> 条</span>
        </div>
      </div>
      <el-button
        type="primary"
        icon="el-icon-plus"
        size="small"
        @click="openCreate"
      >新建题材</el-button>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      style="margin: 0 0 12px; text-align: left"
    />

    <div class="dict-toolbar">
      <el-input
        v-model="search"
        size="small"
        clearable
        prefix-icon="el-icon-search"
        placeholder="搜索题材 / 分组"
        style="width: 220px"
      />
      <el-select v-model="categoryFilter" size="small" clearable placeholder="全部分组" style="width: 150px">
        <el-option v-for="c in categories" :key="c" :label="c" :value="c" />
      </el-select>
      <el-checkbox v-model="showInactive">显示停用</el-checkbox>
      <span class="dict-tip">分组仅用于归档展示，不影响筛选</span>
    </div>

    <!-- 题材卡片网格 -->
    <div class="dict-grid" v-loading="loading" element-loading-text="加载题材字典...">
      <div v-if="!filteredThemes.length && !loading" class="dict-empty">
        <i class="el-icon-collection-tag" />
        <p>暂无题材，点击右上角「新建题材」创建第一个</p>
      </div>
      <div
        v-for="t in filteredThemes"
        :key="t.id"
        class="dict-card"
        :class="{ 'is-inactive': t.status !== 'active' }"
      >
        <div class="card-top">
          <div class="card-name-row">
            <span class="card-name" :title="t.name">{{ t.name }}</span>
            <el-tag size="mini" effect="plain" type="info">{{ t.category }}</el-tag>
            <el-tag v-if="t.status !== 'active'" size="mini" type="info">停用</el-tag>
          </div>
          <div class="card-count">
            <span class="count-num">{{ t.stockCount }}</span>
            <span class="count-label">只个股</span>
          </div>
        </div>
        <div class="card-desc" :title="t.description">
          {{ t.description || '（未填写边界说明）' }}
        </div>
        <div class="card-foot">
          <span class="card-time">{{ t.updatedAt || t.createdAt }}</span>
          <div class="card-actions">
            <el-button v-if="t.status === 'active'" type="text" size="mini" @click="openEdit(t)">编辑</el-button>
            <el-button v-if="t.status === 'active'" type="text" size="mini" @click="openMerge(t)">合并</el-button>
            <el-button
              v-if="t.status === 'active'"
              type="text"
              size="mini"
              class="act-danger"
              @click="toggleStatus(t, 'inactive')"
            >停用</el-button>
            <el-button
              v-else
              type="text"
              size="mini"
              @click="toggleStatus(t, 'active')"
            >启用</el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 新建 / 编辑对话框 -->
    <el-dialog
      :title="dialogMode === 'create' ? '新建题材' : '编辑题材'"
      :visible.sync="dialogVisible"
      width="460px"
    >
      <el-form label-width="72px" size="small">
        <el-form-item label="名称" required>
          <el-input v-model="dialogForm.name" maxlength="30" show-word-limit placeholder="如：存储" />
        </el-form-item>
        <el-form-item label="分组">
          <el-select
            v-model="dialogForm.category"
            filterable
            allow-create
            default-first-option
            style="width: 100%"
            placeholder="选择已有分组或输入新分组"
          >
            <el-option v-for="c in categories" :key="c" :label="c" :value="c" />
          </el-select>
        </el-form-item>
        <el-form-item label="边界说明" required>
          <el-input
            v-model="dialogForm.description"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="什么算、什么不算。如：存储芯片设计与制造，含 HBM；不含封测"
          />
        </el-form-item>
      </el-form>
      <template slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="saveDialog">保存</el-button>
      </template>
    </el-dialog>

    <!-- 合并对话框 -->
    <el-dialog title="合并题材" :visible.sync="mergeVisible" width="460px">
      <div class="merge-from">
        将 <b>{{ mergeFrom ? mergeFrom.name : '' }}</b>
        <span class="merge-meta">（{{ mergeFrom ? mergeFrom.stockCount : 0 }} 只个股）</span>
        合并到：
      </div>
      <el-select v-model="mergeToId" style="width: 100%" size="small" placeholder="选择目标题材">
        <el-option
          v-for="t in mergeTargets"
          :key="t.id"
          :label="`${t.name}（${t.stockCount} 只）`"
          :value="t.id"
        />
      </el-select>
      <el-alert
        type="warning"
        :closable="false"
        style="margin-top: 12px"
        title="合并后：个股打标自动迁移（已有目标标签的跳过），来源题材停用并改名留痕，操作不可自动撤销"
      />
      <template slot="footer">
        <el-button size="small" @click="mergeVisible = false">取消</el-button>
        <el-button type="danger" size="small" :disabled="!mergeToId" :loading="saving" @click="doMerge">确认合并</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { request } from '../utils/http.js'

export default {
  name: 'ThemeDict',
  data() {
    return {
      loading: false,
      saving: false,
      error: '',
      themes: [],
      search: '',
      categoryFilter: null,
      showInactive: false,
      dialogVisible: false,
      dialogMode: 'create',
      dialogId: null,
      dialogForm: { name: '', category: '未分组', description: '' },
      mergeVisible: false,
      mergeFrom: null,
      mergeToId: null,
    }
  },
  computed: {
    categories() {
      return [...new Set(this.themes.map((t) => t.category))].sort()
    },
    activeCount() {
      return this.themes.filter((t) => t.status === 'active').length
    },
    inactiveCount() {
      return this.themes.filter((t) => t.status !== 'active').length
    },
    totalTags() {
      return this.themes.reduce((s, t) => s + t.stockCount, 0)
    },
    filteredThemes() {
      let rows = this.themes
      if (!this.showInactive) rows = rows.filter((t) => t.status === 'active')
      const kw = this.search.trim().toLowerCase()
      if (kw) {
        rows = rows.filter(
          (t) => t.name.toLowerCase().includes(kw) || (t.category || '').toLowerCase().includes(kw),
        )
      }
      if (this.categoryFilter) rows = rows.filter((t) => t.category === this.categoryFilter)
      return rows
    },
    mergeTargets() {
      if (!this.mergeFrom) return []
      return this.themes.filter(
        (t) => t.id !== this.mergeFrom.id && t.status === 'active',
      )
    },
  },
  async created() {
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      this.error = ''
      try {
        const data = await request('/api/theme/dict')
        this.themes = data.list || []
      } catch (e) {
        this.error = e.message || String(e)
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.dialogMode = 'create'
      this.dialogId = null
      this.dialogForm = { name: '', category: this.categoryFilter || '未分组', description: '' }
      this.dialogVisible = true
    },
    openEdit(t) {
      this.dialogMode = 'edit'
      this.dialogId = t.id
      this.dialogForm = { name: t.name, category: t.category, description: t.description }
      this.dialogVisible = true
    },
    async saveDialog() {
      const { name, category, description } = this.dialogForm
      if (!String(name || '').trim()) {
        this.$message.warning('请填写题材名称')
        return
      }
      if (!String(description || '').trim()) {
        this.$message.warning('边界说明必填：说明什么算、什么不算')
        return
      }
      this.saving = true
      try {
        const body = JSON.stringify({ name, category, description })
        if (this.dialogMode === 'create') {
          await request('/api/theme/dict', { method: 'POST', body })
          this.$message.success(`已创建「${name}」`)
        } else {
          await request(`/api/theme/dict/${this.dialogId}`, { method: 'PUT', body })
          this.$message.success('已保存')
        }
        this.dialogVisible = false
        await this.loadData()
      } catch (e) {
        this.$message.error(e.message || String(e))
      } finally {
        this.saving = false
      }
    },
    openMerge(t) {
      this.mergeFrom = t
      this.mergeToId = null
      this.mergeVisible = true
    },
    async doMerge() {
      if (!this.mergeFrom || !this.mergeToId) return
      const target = this.themes.find((t) => t.id === this.mergeToId)
      this.saving = true
      try {
        const data = await request('/api/theme/dict/merge', {
          method: 'POST',
          body: JSON.stringify({ fromId: this.mergeFrom.id, toId: this.mergeToId }),
        })
        this.$message.success(`已合并：迁移 ${data.moved} 只，跳过 ${data.skipped} 只`)
        this.mergeVisible = false
        if (target) this.$notify({
          title: '合并完成',
          message: `「${this.mergeFrom.name}」已并入「${target.name}」，来源题材已停用留痕`,
          type: 'success',
        })
        await this.loadData()
      } catch (e) {
        this.$message.error(e.message || String(e))
      } finally {
        this.saving = false
      }
    },
    toggleStatus(t, status) {
      const label = status === 'inactive' ? '停用' : '启用'
      this.$confirm(
        status === 'inactive'
          ? `停用「${t.name}」后：该题材从打标与筛选中隐藏（保留 ${t.stockCount} 只个股数据，不删除）。确定停用？`
          : `重新启用「${t.name}」？`,
        `${label}题材`,
        { type: 'warning' },
      ).then(async () => {
        try {
          await request(`/api/theme/dict/${t.id}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status }),
          })
          this.$message.success(`已${label}`)
          await this.loadData()
        } catch (e) {
          this.$message.error(e.message || String(e))
        }
      }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.theme-dict {
  text-align: left;
}

.dict-header {
  background: linear-gradient(135deg, #f0f5ff 0%, #fff 60%);
  border: 1px solid #d9e2f5;
  border-radius: 6px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.intro-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.intro-desc {
  font-size: 13px;
  color: #606266;
  margin-top: 4px;
  max-width: 640px;
  line-height: 1.6;
}

.intro-stats {
  display: flex;
  gap: 20px;
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}

.intro-stats b {
  color: #409eff;
  font-size: 14px;
  margin-right: 2px;
}

.dict-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.dict-tip {
  font-size: 12px;
  color: #c0c4cc;
}

.dict-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  min-height: 200px;
}

.dict-empty {
  grid-column: 1 / -1;
  background: #fff;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  padding: 56px 0;
  text-align: center;
  color: #c0c4cc;
}

.dict-empty i {
  font-size: 40px;
}

.dict-empty p {
  font-size: 13px;
  margin: 12px 0 0;
}

.dict-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.15s, border-color 0.15s;
}

.dict-card:hover {
  border-color: #b3d8ff;
  box-shadow: 0 2px 12px rgba(64, 158, 255, 0.12);
}

.dict-card.is-inactive {
  background: #fafafa;
  opacity: 0.72;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.card-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
}

.card-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  word-break: break-all;
}

.card-count {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  line-height: 1.1;
}

.count-num {
  font-size: 20px;
  font-weight: 700;
  color: #409eff;
}

.count-label {
  font-size: 11px;
  color: #c0c4cc;
}

.card-desc {
  flex: 1;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
  margin: 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 38px;
}

.card-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #f2f6fc;
  padding-top: 8px;
}

.card-time {
  font-size: 11px;
  color: #c0c4cc;
}

.card-actions {
  display: flex;
  gap: 2px;
}

.act-danger {
  color: #f56c6c;
}

.merge-from {
  font-size: 14px;
  color: #303133;
  margin-bottom: 12px;
  line-height: 1.7;
}

.merge-meta {
  font-size: 12px;
  color: #909399;
  margin-left: 2px;
}
</style>
